import { Environment, Lightformer, Text, useGLTF, useTexture } from '@react-three/drei';
import { Canvas, extend, useFrame, useThree } from '@react-three/fiber';
import { BallCollider, CuboidCollider, Physics, RigidBody, useRopeJoint, useSphericalJoint } from '@react-three/rapier';
import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import { MeshLineGeometry, MeshLineMaterial } from 'meshline';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import logger from '@/utils/logger';

// Enable ColorManagement globally
THREE.ColorManagement.enabled = true;

extend({ MeshLineGeometry, MeshLineMaterial });
const _MODEL_URL = '/blender3.glb';

useGLTF.preload(
  'https://assets.vercel.com/image/upload/contentful/image/e5382hct74si/5huRVDzcoDwnbgrKUo1Lzs/53b6dd7d6b4ffcdbd338fa60265949e1/tag.glb',
);
useTexture.preload(
  'https://assets.vercel.com/image/upload/contentful/image/e5382hct74si/SOT1hmCesOHxEYxL7vkoZ/c57b29c85912047c414311723320c16b/band.jpg',
);

function captureCanvas(canvasRef) {
  if (!canvasRef.current) return null;
  const canvas = canvasRef.current;
  // Force a render to ensure the canvas is up to date
  canvas.render();
  return canvas.toDataURL('image/png');
}

const encodeParams = (params) => {
  // First convert the JSON string to a URI-encoded string to handle Unicode characters
  const jsonString = JSON.stringify(params);
  const uriEncoded = encodeURIComponent(jsonString);
  const encoded = btoa(uriEncoded);
  return encoded.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
};

const decodeParams = (encoded) => {
  try {
    const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(padded);
    // Decode the URI-encoded string back to the original JSON string
    const jsonString = decodeURIComponent(decoded);
    return JSON.parse(jsonString);
  } catch (_error) {
    logger.error(new Error('Invalid URL parameters'));
    return null;
  }
};

function LinkedInModal({ isOpen, onClose, previewImage, type, url }) {
  const [copied, setCopied] = useState(false);
  const suggestedText = `I'm excited to share that I've been recognized as a ClickUp ${
    type === 'champions' ? 'Champion' : type === 'consultants' ? 'Verified Consultant' : 'Power User'
  }! 🎉\n\nCheck out my recognition card: ${url}\n\n#ClickUp #Productivity #Recognition`;

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(suggestedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error(error, { context: 'copyText' });
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-xs z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-black rounded-xl w-full max-w-md overflow-hidden"
      >
        <div className="px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-medium text-white">Share to LinkedIn</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Suggested Text</label>
            <div className="relative">
              <textarea
                readOnly
                value={suggestedText}
                className="w-full h-32 px-4 py-3 pr-20 bg-black/50 border border-white/10 rounded-lg 
                  text-white text-sm resize-none"
              />
              <button
                onClick={handleCopyText}
                className="absolute top-3 right-3 z-10 px-3 py-1 rounded text-sm font-medium 
                  bg-white/10 hover:bg-white/20 text-white transition-all"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          <a
            href="https://www.linkedin.com/share/new/"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center px-4 py-3 rounded-lg font-medium text-sm 
              bg-[#0A66C2] hover:bg-[#0A66C2]/90 text-white transition-colors"
          >
            Open LinkedIn
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ShareModal({ isOpen, onClose, url, previewImage, type, firstName, lastName }) {
  const [copied, setCopied] = useState(false);
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const bgColor = type === 'champions' ? 'from-[#5982FD]/5 to-transparent' : 'from-[#AB22F3]/5 to-transparent';
  const buttonColor = type === 'champions' ? 'bg-[#5982FD]' : 'bg-[#AB22F3]';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error(error, { context: 'copyLink' });
    }
  };

  const handleEmailShare = () => {
    const subject = 'Check out my ClickUp Recognition!';
    const body = `I've been recognized as a ClickUp ${
      type === 'champions' ? 'Champion' : type === 'consultants' ? 'Verified Consultant' : 'Power User'
    }! Check it out here: ${url}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const handleLinkedInShare = () => {
    setShowLinkedInModal(true);
  };

  const handleTwitterShare = () => {
    const text = `I've been recognized as a ClickUp ${
      type === 'champions' ? 'Champion' : type === 'consultants' ? 'Verified Consultant' : 'Power User'
    }!`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text,
    )}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-xs z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-black/90 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden
            border border-white/10 relative"
        >
          {/* Background gradient */}
          <div className={`absolute inset-0 bg-linear-to-b ${bgColor} pointer-events-none`} />

          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center relative">
            <h3 className="text-lg font-semibold text-white">Share Recognition</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Preview */}
          {previewImage && (
            <div className="px-6 pt-6">
              <div
                className="rounded-lg overflow-hidden border border-white/10 shadow-xl 
                bg-linear-to-b from-black to-gray-900"
              >
                <img
                  src={previewImage}
                  alt="Recognition Preview"
                  className="w-full object-cover"
                  style={{ maxHeight: '300px' }}
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">Recognition Link</label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    readOnly
                    value={url}
                    className="w-full px-4 py-2.5 bg-black border border-white/10 rounded-lg 
                      text-gray-300 text-sm focus:ring-2 focus:ring-white/20 focus:border-white/20"
                  />
                </div>
                <button
                  onClick={handleCopy}
                  className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all
                    ${
                      copied
                        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                        : `${buttonColor} text-white hover:opacity-90`
                    }`}
                >
                  {copied ? (
                    <span className="flex items-center gap-1">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Copied
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Copy Link
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-gray-400">Share directly to</p>
              <div className="flex gap-3">
                {[
                  {
                    name: 'Email',
                    icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
                    onClick: handleEmailShare,
                  },
                  {
                    name: 'LinkedIn',
                    icon: 'M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z M2 4h4v4H2z',
                    onClick: handleLinkedInShare,
                  },
                  {
                    name: 'Twitter',
                    icon: 'M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z',
                    onClick: handleTwitterShare,
                  },
                ].map((platform) => (
                  <button
                    key={platform.name}
                    onClick={platform.onClick}
                    className="flex-1 py-2.5 px-4 rounded-lg border border-white/10 
                      hover:bg-white/5 transition-colors text-gray-300 text-sm font-medium
                      bg-gray-800/50"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d={platform.icon} />
                      </svg>
                      {platform.name}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
      <AnimatePresence>
        {showLinkedInModal && (
          <LinkedInModal
            isOpen={showLinkedInModal}
            onClose={() => setShowLinkedInModal(false)}
            previewImage={previewImage}
            type={type}
            url={url}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export function Band({ maxSpeed = 50, minSpeed = 10, firstName, lastName, type }) {
  const band = useRef(),
    fixed = useRef(),
    j1 = useRef(),
    j2 = useRef(),
    j3 = useRef(),
    card = useRef(); // prettier-ignore
  const vec = new THREE.Vector3(),
    ang = new THREE.Vector3(),
    rot = new THREE.Vector3(),
    dir = new THREE.Vector3(); // prettier-ignore
  const segmentProps = {
    type: 'dynamic',
    canSleep: true,
    colliders: false,
    angularDamping: 2,
    linearDamping: 2,
  };
  const { nodes, materials } = useGLTF('/CU1.glb');

  const texture = useTexture(
    type === 'champions'
      ? '/ChampionLanyard.png'
      : type === 'consultants'
        ? '/ConsultantBand.png'
        : '/PowerUserLanyard.png',
  );
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping; // Ensure the texture repeats

  const { width, height } = useThree((state) => state.size);
  const [curve] = useState(
    () =>
      new THREE.CatmullRomCurve3([new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()]),
  );
  const [dragged, drag] = useState(false);
  const [hovered, hover] = useState(false);

  useRopeJoint(fixed, j1, [[0, 0, 0], [0, 0, 0], 1]); // prettier-ignore
  useRopeJoint(j1, j2, [[0, 0, 0], [0, 0, 0], 1]); // prettier-ignore
  useRopeJoint(j2, j3, [[0, 0, 0], [0, 0, 0], 1]); // prettier-ignore
  useSphericalJoint(j3, card, [
    [0, 0, 0],
    [0, 1.45, 0],
  ]); // prettier-ignore

  useEffect(() => {
    if (hovered) {
      document.body.style.cursor = dragged ? 'grabbing' : 'grab';
      return () => void (document.body.style.cursor = 'auto');
    }
  }, [hovered, dragged]);

  useFrame((state, delta) => {
    if (dragged) {
      vec.set(state.pointer.x, state.pointer.y, 0.5).unproject(state.camera);
      dir.copy(vec).sub(state.camera.position).normalize();
      vec.add(dir.multiplyScalar(state.camera.position.length()));
      [card, j1, j2, j3, fixed].forEach((ref) => ref.current?.wakeUp());
      card.current?.setNextKinematicTranslation({
        x: vec.x - dragged.x,
        y: vec.y - dragged.y,
        z: vec.z - dragged.z,
      });
    }
    if (fixed.current) {
      // Fix most of the jitter when over pulling the card
      [j1, j2].forEach((ref) => {
        if (!ref.current.lerped) ref.current.lerped = new THREE.Vector3().copy(ref.current.translation());
        const clampedDistance = Math.max(0.1, Math.min(1, ref.current.lerped.distanceTo(ref.current.translation())));
        ref.current.lerped.lerp(
          ref.current.translation(),
          delta * (minSpeed + clampedDistance * (maxSpeed - minSpeed)),
        );
      });
      // Calculate catmul curve
      curve.points[0].copy(j3.current.translation());
      curve.points[1].copy(j2.current.lerped);
      curve.points[2].copy(j1.current.lerped);
      curve.points[3].copy(fixed.current.translation());
      band.current.geometry.setPoints(curve.getPoints(32));
      // Tilt it back towards the screen
      ang.copy(card.current.angvel());
      rot.copy(card.current.rotation());
      card.current.setAngvel({ x: ang.x, y: ang.y - rot.y * 0.25, z: ang.z });
    }
  });

  curve.curveType = 'chordal';
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;

  // Update texture loading to be dynamic based on type
  const cardTexture = useTexture(
    type === 'champions'
      ? '/ChampionTestNew.png'
      : type === 'consultants'
        ? '/ConsultantLanyard.png'
        : '/PowerTestNew.png',
  );

  // Updated texture configuration
  useEffect(() => {
    if (cardTexture) {
      cardTexture.flipY = false;
      cardTexture.rotation = 0;
      cardTexture.repeat.set(1, 1);
      cardTexture.offset.set(0, 0);
      cardTexture.center.set(0.5, 0.5);
      cardTexture.generateMipmaps = true;
      cardTexture.minFilter = THREE.LinearMipmapLinearFilter;
      cardTexture.magFilter = THREE.LinearFilter;
      cardTexture.anisotropy = 16;
      cardTexture.needsUpdate = true; // Force texture update
    }
  }, [cardTexture]);

  // Also update the lanyard texture configuration
  useEffect(() => {
    if (texture) {
      texture.flipY = false;
      texture.rotation = 0;
      texture.repeat.set(-3, 1);
      texture.offset.set(0, 0);
      texture.center.set(0.5, 0.5);
      texture.needsUpdate = true; // Force texture update
    }
  }, [texture]);

  curve.curveType = 'chordal';
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;

  // Add a rotation to the card group to ensure correct orientation
  const cardRotation = useMemo(() => new THREE.Euler(0, 0, 0), []);

  return (
    <>
      <group position={[0, 4, 0]}>
        <RigidBody ref={fixed} {...segmentProps} type="fixed" />
        <RigidBody position={[0.5, 0, 0]} ref={j1} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[1, 0, 0]} ref={j2} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[1.5, 0, 0]} ref={j3} {...segmentProps}>
          <BallCollider args={[0.1]} />
        </RigidBody>
        <RigidBody position={[2, 0, 0]} ref={card} {...segmentProps} type={dragged ? 'kinematicPosition' : 'dynamic'}>
          <CuboidCollider args={[0.8, 1.125, 0.01]} />
          <group
            scale={2.25}
            position={[0, -1.2, -0.05]}
            rotation={cardRotation}
            onPointerOver={() => hover(true)}
            onPointerOut={() => hover(false)}
            onPointerDown={(e) => {
              e.target.setPointerCapture(e.pointerId);
              const dragPoint = new THREE.Vector3().copy(e.point).sub(vec.copy(card.current.translation()));
              drag(dragPoint);
            }}
            onPointerUp={(e) => {
              e.target.releasePointerCapture(e.pointerId);
              drag(false);
            }}
          >
            <mesh geometry={nodes.card.geometry}>
              <meshPhysicalMaterial
                map={cardTexture}
                map-anisotropy={16}
                clearcoat={1}
                clearcoatRoughness={0.15}
                roughness={0.3}
                metalness={0.8}
                envMapIntensity={1.5}
                side={THREE.DoubleSide}
              />
            </mesh>
            <Text
              position={[-0.3, 0.3, 0.01]}
              fontSize={0.06}
              color={type === 'champions' ? '#3559C7' : type === 'consultants' ? '#D73659' : '#8101C2'}
              anchorX="left"
              anchorY="bottom"
              fontWeight="bold"
              maxWidth={0.6}
              textAlign="left"
              width={0.6}
              height={0.2}
              clipRect
              material-metalness={0.8}
              material-roughness={0.3}
              material-clearcoat={1}
              material-clearcoatRoughness={0.15}
              material-envMapIntensity={1.5}
            >
              {firstName}
              {`\n`}
              {lastName}
            </Text>
            <mesh geometry={nodes.clip.geometry}>
              <meshPhysicalMaterial color="#e0e0e0" roughness={0.3} metalness={1} envMapIntensity={1.5} />
            </mesh>
            <mesh geometry={nodes.clamp.geometry}>
              <meshPhysicalMaterial color="#e0e0e0" roughness={0.3} metalness={1} envMapIntensity={1.5} />
            </mesh>
          </group>
        </RigidBody>
      </group>
      <mesh ref={band}>
        <meshLineGeometry />
        <meshLineMaterial
          color="white"
          depthTest={false}
          resolution={[width, height]}
          useMap
          map={texture}
          repeat={[-3, -1]}
          lineWidth={1}
        />
      </mesh>
    </>
  );
}

export default function Index() {
  const router = useRouter();
  const { code, firstName: rawFirstName, lastName: rawLastName, type: rawType } = router.query;

  // Try to get parameters from either encoded or non-encoded format
  const params = useMemo(() => {
    if (code) {
      // Try decoded format first
      const decodedParams = decodeParams(code);
      if (decodedParams) return decodedParams;
    }

    // Fall back to raw URL parameters if they exist
    if (rawFirstName || rawLastName || rawType) {
      return {
        firstName: rawFirstName,
        lastName: rawLastName,
        type: rawType,
      };
    }

    // Use defaults if neither format is present
    return {
      firstName: 'John',
      lastName: 'Doe',
      type: 'powerUsers',
    };
  }, [code, rawFirstName, rawLastName, rawType]);

  const { firstName, lastName, type } = params;

  const [showText, setShowText] = useState(true);
  const [showCard, setShowCard] = useState(false);
  const { debug } = useState({ debug: false });
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const canvasRef = useRef(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Generate only the encoded URL for sharing
  const shareUrl = useMemo(() => {
    const baseUrl = `${window.location.origin}/card`;
    const encodedParams = encodeParams({ firstName, lastName, type });
    return `${baseUrl}/${encodedParams}`;
  }, [firstName, lastName, type]);

  useEffect(() => {
    const textTimer = setTimeout(() => {
      setShowText(false);
    }, 2500);

    const cardTimer = setTimeout(() => {
      setShowCard(true);

      // Delay confetti animation
      setTimeout(() => {
        const duration = 5 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = {
          startVelocity: 30,
          spread: 360,
          ticks: 60,
          zIndex: 9999,
        };

        const randomInRange = (min, max) => Math.random() * (max - min) + min;

        const interval = setInterval(() => {
          const timeLeft = animationEnd - Date.now();

          if (timeLeft <= 0) {
            return clearInterval(interval);
          }

          const particleCount = 50 * (timeLeft / duration);
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          });
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          });
        }, 250);

        return () => clearInterval(interval);
      }, 1800); // 1.8 second delay before starting confetti
    }, 3000);

    return () => {
      clearTimeout(textTimer);
      clearTimeout(cardTimer);
    };
  }, []);

  const handleShareClick = useCallback(() => {
    if (canvasRef.current) {
      const screenshot = captureCanvas(canvasRef.current);
      setPreviewImage(screenshot);
      setIsShareModalOpen(true);
    }
  }, []);

  useEffect(() => {
    // Prevent scrolling and bouncing effects
    const preventDefault = (e) => e.preventDefault();

    // Add event listeners to prevent all touch and scroll events
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.touchAction = 'none';
    document.documentElement.style.overflow = 'hidden'; // Prevent html element scroll
    document.documentElement.style.touchAction = 'none';

    // Prevent pull-to-refresh
    document.addEventListener('touchmove', preventDefault, { passive: false });
    document.addEventListener('scroll', preventDefault, { passive: false });

    return () => {
      // Cleanup
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.touchAction = '';
      document.removeEventListener('touchmove', preventDefault);
      document.removeEventListener('scroll', preventDefault);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-black touch-none overscroll-none"
      onTouchMove={(e) => e.preventDefault()}
      onScroll={(e) => e.preventDefault()}
    >
      <AnimatePresence>
        {showText && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              duration: 0.8,
              ease: 'easeOut',
            }}
            className="fixed inset-0 flex items-center justify-center bg-black z-10"
          >
            <motion.h1
              className="text-6xl font-medium text-center"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{
                duration: 0.8,
                ease: 'easeOut',
              }}
            >
              <span className="text-white">Thank you for being a</span>
              <br />
              <span
                className={`font-bold mt-4 block ${
                  type === 'champions' ? 'text-[#5982FD]' : type === 'consultants' ? 'text-[#D73659]' : 'text-[#AB22F3]'
                }`}
              >
                ClickUp {type === 'champions' ? 'Champion' : type === 'consultants' ? 'vc  Consultant' : 'Power User'}!
              </span>
            </motion.h1>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 1,
              ease: 'easeOut',
            }}
            className="fixed inset-0 w-full h-full"
          >
            {/* Back Button */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: showCard ? 1 : 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              onClick={() => router.push('/HomeDesktop')}
              className="fixed top-8 left-8 z-20 bg-white/10 backdrop-blur-md 
                hover:bg-white/20 transition-all duration-300 rounded-full p-4 
                flex items-center gap-2 text-white border border-white/20"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 12H5M5 12L11 18M5 12L11 6" />
              </svg>
              Back
            </motion.button>

            <Canvas ref={canvasRef} camera={{ position: [0, 0, 13], fov: 25 }} className="w-full h-full">
              <color attach="background" args={['#000000']} />
              <ambientLight intensity={Math.PI} />
              <Physics debug={debug} interpolate gravity={[0, -40, 0]} timeStep={1 / 60}>
                <Band firstName={firstName} lastName={lastName} type={type} />
              </Physics>
              <Environment background blur={0.75}>
                <color attach="background" args={['black']} />
                <Lightformer
                  intensity={2}
                  color="white"
                  position={[0, -1, 5]}
                  rotation={[0, 0, Math.PI / 3]}
                  scale={[100, 0.1, 1]}
                />
                <Lightformer
                  intensity={3}
                  color="white"
                  position={[-1, -1, 1]}
                  rotation={[0, 0, Math.PI / 3]}
                  scale={[100, 0.1, 1]}
                />
                <Lightformer
                  intensity={3}
                  color="white"
                  position={[1, 1, 1]}
                  rotation={[0, 0, Math.PI / 3]}
                  scale={[100, 0.1, 1]}
                />
                <Lightformer
                  intensity={10}
                  color="white"
                  position={[-10, 0, 14]}
                  rotation={[0, Math.PI / 2, Math.PI / 3]}
                  scale={[100, 10, 1]}
                />
              </Environment>
            </Canvas>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: showCard ? 1 : 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        onClick={handleShareClick}
        className="fixed bottom-8 right-8 z-20 bg-white/10 backdrop-blur-md 
          hover:bg-white/20 transition-all duration-300 rounded-full p-4 
          flex items-center gap-2 text-white border border-white/20"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
          <polyline points="16 6 12 2 8 6" />
          <line x1="12" y1="2" x2="12" y2="15" />
        </svg>
        Share
      </motion.button>

      <AnimatePresence>
        {isShareModalOpen && (
          <ShareModal
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            url={shareUrl}
            previewImage={previewImage}
            type={type}
            firstName={firstName}
            lastName={lastName}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
